// Pixel-accurate text measurement via a shared offscreen canvas (KIR-2563).
//
// `CanvasRenderingContext2D.measureText` returns the rendered width of a string
// for a given CSS font without touching the DOM, which is exactly what
// width-measured path truncation needs. Because zoom scales the font and the
// available width together (both in CSS pixels), measurements stay consistent
// across browser zoom levels.

// `undefined` = not yet resolved, `null` = unavailable (e.g. jsdom / SSR).
let cachedContext: CanvasRenderingContext2D | null | undefined;

// Rough per-character width used only when a real canvas context is
// unavailable (jsdom in tests, SSR). Production always has a canvas.
const FALLBACK_CHAR_WIDTH_PX = 7;

// Fallback CSS font applied when the caller hasn't resolved the element's real
// font yet (first paint). Without this the canvas would silently measure with
// its default `10px sans-serif` — or worse, whatever font a previous caller
// left on the shared context — producing an off, cross-cell-contaminated width.
export const DEFAULT_MEASURE_FONT = '14px monospace';

function getContext(): CanvasRenderingContext2D | null {
  if (cachedContext !== undefined) {
    return cachedContext;
  }
  if (typeof document === 'undefined') {
    cachedContext = null;
    return cachedContext;
  }
  cachedContext = document.createElement('canvas').getContext('2d');
  return cachedContext;
}

/**
 * Return the rendered width, in CSS pixels, of `text` when painted with `font`
 * (a CSS shorthand font string, e.g. `"12px monospace"`). Falls back to a
 * proportional estimate when no canvas context is available.
 */
export function measureTextWidth(text: string, font: string): number {
  const context = getContext();
  if (!context) {
    return text.length * FALLBACK_CHAR_WIDTH_PX;
  }
  // Always assign a font: an empty string would leave the shared context on a
  // previous caller's font, so seed a sane default instead.
  context.font = font || DEFAULT_MEASURE_FONT;
  return context.measureText(text).width;
}

// Test-only: drop the memoized context so a test can install a fake canvas.
export function resetMeasureTextCacheForTests(): void {
  cachedContext = undefined;
}
